import { z } from "zod";
import type { IfiFormStep } from "./ui-state.js";

const numberLikeSchema = z.union([z.string(), z.number()]).optional();
const consentSchema = z
  .boolean()
  .refine((value) => value, "Le consentement est obligatoire.");

export const ifiContactStepSchema = z.object({
  email: z.string().email("Email invalide."),
  firstName: z.string().trim().max(120, "Le prenom est trop long."),
  source: z
    .string()
    .trim()
    .min(1, "La source est obligatoire.")
    .max(120, "La source est trop longue."),
  consent: consentSchema,
  honeypot: z.string().max(0, "Requete invalide.").optional()
});

export const ifiCountryStepSchema = z.object({
  country: z.string().trim().min(1, "Le pays de residence est obligatoire."),
});

export const ifiPatrimoineFranceStepSchema = z
  .object({
    residencePrincipale: numberLikeSchema,
    autresBiens: numberLikeSchema,
  })
  .superRefine((input, ctx) => {
    validateRequiredNonNegative(
      "residencePrincipale",
      input.residencePrincipale,
      ctx,
    );
    validateRequiredNonNegative("autresBiens", input.autresBiens, ctx);
  });

export const ifiActifsStepSchema = z.object({
  actifs: numberLikeSchema
}).superRefine((input, ctx) => {
  validateRequiredNonNegative("actifs", input.actifs, ctx);
});

export const ifiPassifsStepSchema = z.object({
  passifs: numberLikeSchema
}).superRefine((input, ctx) => {
  validateRequiredNonNegative("passifs", input.passifs, ctx);
  });

export const ifiFormSchema = z
  .object({
    email: z.string().email("Email invalide."),
    firstName: z.string().trim().max(120, "Le prenom est trop long."),
    source: z
      .string()
      .trim()
      .min(1, "La source est obligatoire.")
      .max(120, "La source est trop longue."),
    country: z.string().trim().min(1, "Le pays de residence est obligatoire."),
    consent: consentSchema,
    honeypot: z.string().max(0).optional(),
    residencePrincipale: numberLikeSchema,
    autresBiens: numberLikeSchema,
    actifs: numberLikeSchema,
    passifs: numberLikeSchema
  })
  .superRefine((input, ctx) => {
    if (isFranceCountry(input.country)) {
      validateRequiredNonNegative(
        "residencePrincipale",
        input.residencePrincipale,
        ctx,
      );
      validateRequiredNonNegative("autresBiens", input.autresBiens, ctx);
    } else {
      validateRequiredNonNegative("actifs", input.actifs, ctx);
    }
    validateRequiredNonNegative("passifs", input.passifs, ctx);
  });

export const ifiStepSchemas: Record<IfiFormStep, z.ZodTypeAny> = {
  intro: z.object({}),
  country: ifiCountryStepSchema,
  "patrimoine-france": ifiPatrimoineFranceStepSchema,
  actifs: ifiActifsStepSchema,
  passifs: ifiPassifsStepSchema,
  contact: ifiContactStepSchema,
  result: z.object({}),
  "expert-optin": z.object({}),
};

function parseNumberLike(value: string | number | undefined): number | undefined {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }

  const parsed =
    typeof value === "number" ? value : Number(value.replaceAll(/\s+/g, ""));
  if (!Number.isFinite(parsed)) {
    return Number.NaN;
  }

  return parsed;
}

function isValidNonNegative(value: number | undefined): boolean {
  return value === undefined || (Number.isFinite(value) && value >= 0);
}

function hasProvidedValue(value: string | number | undefined): boolean {
  if (value === undefined || value === null) {
    return false;
  }

  if (typeof value === "string") {
    return value.trim().length > 0;
  }

  return true;
}

function validateOptionalNonNegative(
  fieldName: "residencePrincipale" | "autresBiens" | "actifs" | "passifs",
  value: string | number | undefined,
  ctx: z.RefinementCtx
): number | undefined {
  const parsedValue = parseNumberLike(value);
  if (!isValidNonNegative(parsedValue) && hasProvidedValue(value)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `\`${fieldName}\` doit etre un nombre positif.`
    });
  }

  return parsedValue;
}

function validateRequiredNonNegative(
  fieldName: "residencePrincipale" | "autresBiens" | "actifs" | "passifs",
  value: string | number | undefined,
  ctx: z.RefinementCtx
): number | undefined {
  if (!hasProvidedValue(value)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `\`${fieldName}\` est obligatoire.`
    });
    return undefined;
  }

  return validateOptionalNonNegative(fieldName, value, ctx);
}

function isFranceCountry(value: string): boolean {
  const normalized = value.trim().toUpperCase();
  return normalized === "FR" || normalized === "FRANCE";
}
